import React, { useState, useEffect } from 'react';
import { Outlet, NavLink as RouterNavLink, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuthCliente } from '../context/AuthClienteContext';
import { useNotifications } from '../hooks/useNotifications';
import MatchApprovalButton from '../components/MatchApprovalButton';
import LogoutAnimation from '../components/LogoutAnimation';
import SmoothAnalogClock from '../components/SmoothAnalogClock';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale/es';
import { doc, updateDoc, serverTimestamp, deleteField } from 'firebase/firestore';
import VictoryAnimation from '../components/VictoryAnimation';
import LoginSuccessAnimation from '../components/LoginSuccessAnimation';

import {
  Box, Flex, HStack, VStack, Text, Button, Menu, MenuButton, MenuList,
  MenuItem, MenuDivider, Heading, useDisclosure, IconButton, Avatar,
  Tag, Spinner, Spacer, Divider
} from '@chakra-ui/react';
import { HamburgerIcon, CloseIcon, ChevronDownIcon } from '@chakra-ui/icons';
import { FaTicketAlt, FaBell } from 'react-icons/fa';

const mainLinks = [
  { name: 'Inicio', path: '/portal/inicio' },
  { name: 'Ranking', path: '/portal/ranking' },
  { name: 'Retas', path: '/portal/retas-y-torneos' },
  { name: 'Torneos', path: '/portal/torneos' },
  { name: 'Comunidad', path: '/portal/comunidad' },
];

const NavLink = ({ to, children, onClick }) => (
  <Button
    as={RouterNavLink}
    to={to}
    onClick={onClick}
    px={3}
    py={2}
    variant="ghost"
    color="white"
    _hover={{ bg: 'green.700' }}
    _activeLink={{ bg: 'purple.600', fontWeight: 'bold' }}
    w={{ base: 'full', md: 'auto' }}
    justifyContent={{ base: 'start', md: 'center' }}
  >
    {children}
  </Button>
);

export default function PortalLayout() {
  const { currentUser, db, logout, isLoggingOut, finalizaLogout, loginSuccess, clearLoginSuccess } = useAuthCliente();
  const { notifications, unreadCount, markAllAsRead } = useNotifications();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();
  const companyId = currentUser?.companyId;
  const [horaActual, setHoraActual] = useState(new Date());

  useEffect(() => {
  const interval = setInterval(() => {
    setHoraActual(new Date());
  }, 1000);
  return () => clearInterval(interval);
}, []);

  const [showVictory, setShowVictory] = useState(false);

  useEffect(() => {
    if (currentUser?.unseenVictory) {
      setShowVictory(true);
    }
  }, [currentUser]);

  const dismissNotification = async (notifId) => {
    const ref = doc(db, 'companies', companyId, 'end_customers', currentUser.uid, 'notifications', notifId);
    await updateDoc(ref, { dismissed: true, dismissedAt: serverTimestamp() });
  };

  const clearVictoryFlagAndHide = async () => {
    setShowVictory(false);
    if (!currentUser) return;
    const userRef = doc(db, 'companies', currentUser.companyId, 'end_customers', currentUser.uid);
    await updateDoc(userRef, {
      unseenVictory: deleteField()
    });
  };

  const activeNotifs = currentUser ? notifications.filter(n => !n.dismissed) : [];
  const bellItems = activeNotifs.slice(0, 5);

  return (
    <>
      {isLoggingOut && <LogoutAnimation onComplete={finalizaLogout} />}

      {loginSuccess && (
        <LoginSuccessAnimation onComplete={clearLoginSuccess} />
      )}

      {showVictory && currentUser?.unseenVictory && (
        <VictoryAnimation 
          victoryData={currentUser.unseenVictory}
          onComplete={clearVictoryFlagAndHide}
        />
      )}

      <Box minH="100vh" bg="gray.100">
        <Box bg="green.600" color="white" px={4} boxShadow="md" position="sticky" top="0" zIndex="banner">
          <Flex h={16} align="center">
            <HStack spacing={4} align="center">
              <IconButton
                size="md"
                icon={isOpen ? <CloseIcon /> : <HamburgerIcon />}
                aria-label="Open Menu"
                display={{ md: 'none' }}
                onClick={isOpen ? onClose : onOpen}
                variant="outline"
                color="white"
                _hover={{ bg: 'white', color: 'green.600' }}
              />
              <Heading size="md" noOfLines={1} display={{ base: 'none', sm: 'block' }}>
                Aces
              </Heading>
            </HStack>
            <Spacer />

            {currentUser ? (
              <HStack spacing={2} align="center">
                <HStack as="nav" spacing={1} display={{ base: 'none', md: 'flex' }}>
                  {mainLinks.map(link => (
                    <NavLink key={link.name} to={link.path}>{link.name}</NavLink>
                  ))}
                </HStack>

                <Menu>
                  <MenuButton as={Button} variant="ghost" color="white" _hover={{ bg: 'green.700' }} _active={{ bg: 'purple.700' }} rightIcon={<ChevronDownIcon />}>
                    Agenda
                  </MenuButton>
                  <MenuList bg="white" color="gray.800">
                    <MenuItem as={RouterNavLink} to="/portal/mis-reservas">Mi Agenda</MenuItem>
                    <MenuItem as={RouterNavLink} to={`/reservas/${companyId}`}>Reservas</MenuItem>
                  </MenuList>
                </Menu>

                <Button size="sm" bg="white" color="purple" leftIcon={<FaTicketAlt />} pointerEvents="none">
                  Tokens: {currentUser.bookingTokens ?? <Spinner size="xs" />}
                </Button>

                <Menu isLazy onOpen={markAllAsRead}>
                  {/* ... */}
                </Menu>

                <Menu>
                  <MenuButton as={Button} rounded="full" variant="link" cursor="pointer" minW={0}>
                    <Avatar size="sm" name={currentUser.name} src={currentUser.photoURL} />
                  </MenuButton>
                  <MenuList bg="white" color="gray.800">
                    <MenuItem as={RouterNavLink} to="/portal/mi-perfil">Mi Perfil</MenuItem>
                    <MenuDivider />
                    <MenuItem onClick={logout} color="red.500" fontWeight="bold">Cerrar Sesión</MenuItem>
                  </MenuList>
                </Menu>
              </HStack>
            ) : (
              <Button as={RouterNavLink} to="/login" colorScheme="white" variant="outline" _hover={{ bg: 'white', color: 'green.600' }}>
                Iniciar Sesión / Crear Cuenta
              </Button>
            )}
          </Flex>

          {currentUser && (
            <Flex position="relative" left="50%" right="50%" w="100vw" transform="translateX(-50%)" bg="green.700" px={4} py={1} align="center" justify="space-between" flexWrap="wrap" columnGap={4} rowGap={1} color="whiteAlpha.800">
              <Text fontSize="sm" fontWeight="medium" whiteSpace="nowrap">Bienvenid@ {currentUser.name}</Text>
              <HStack spacing={3} align="center">
                <HStack spacing={2} whiteSpace="nowrap">
                  <Text fontSize="sm" textTransform="uppercase">
                    {new Date().toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' })}
                  </Text>
                  <Text fontSize="sm" fontWeight="bold" textTransform="uppercase">
                    {horaActual.toLocaleTimeString('es-MX')}
                  </Text>

                </HStack>
                <SmoothAnalogClock size={45} />
              </HStack>
            </Flex>
          )}

          {isOpen && currentUser && (
            <Box pb={4} display={{ md: 'none' }}>
              <VStack as="nav" spacing={2} align="stretch">
                {mainLinks.map(link => (
                  <NavLink key={link.name} to={link.path} onClick={onClose}>{link.name}</NavLink>
                ))}
                <Divider borderColor="whiteAlpha.300" />
                <Text fontWeight="bold" pt={2} px={3} color="whiteAlpha.700">Agenda</Text>
                <NavLink to="/portal/mis-reservas" onClick={onClose}>Mi Agenda</NavLink>
                <NavLink to={`/reservas/${companyId}`} onClick={onClose}>Reservas</NavLink>
              </VStack>
            </Box>
          )}
        </Box>
        <Box p={{ base: 2, md: 4 }}>
          <Outlet />
        </Box>
      </Box>
    </>
  );
}
